// ============================================================================
// TEMPLATE BASIC INFO COMPONENT
// Form for basic template information (Step 1 of wizard)
// ============================================================================

import React from 'react';
import { FileText, Target, LayoutGrid, Info } from 'lucide-react';
import { TemplateType, TemplateScope } from '../../types';

interface TemplateBasicInfoProps {
  data: {
    name: string;
    description: string;
    purpose: string;
    goal: string;
    category: TemplateType;
    scope: TemplateScope;
  };
  onChange: (updates: Partial<TemplateBasicInfoProps['data']>) => void;
}

export default function TemplateBasicInfo({ data, onChange }: TemplateBasicInfoProps) {
  return (
    <div className="flex flex-col gap-6">
      {/* Card 1: Template Details */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-main">Template Details</h3>
            <p className="text-sm text-text-sub">Basic information about your session template.</p>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {/* Name */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-main">
              Template Name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={data.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="e.g., Product Discovery Workshop"
              className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
            />
          </label>

          {/* Description */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-main">
              Description <span className="text-red-500">*</span>
            </span>
            <textarea
              value={data.description}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="Describe what this template is designed to achieve..."
              rows={3}
              className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
            />
          </label>
        </div>
      </div>

      {/* Card 2: Purpose & Goals */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-main">Purpose & Goals</h3>
            <p className="text-sm text-text-sub">Define the objectives for sessions using this template.</p>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {/* Purpose */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-main">
              Purpose <span className="text-red-500">*</span>
            </span>
            <textarea
              value={data.purpose}
              onChange={(e) => onChange({ purpose: e.target.value })}
              placeholder="What is the main purpose of sessions using this template?"
              rows={2}
              className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
            />
          </label>

          {/* Goal */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-main">
              Goal <span className="text-red-500">*</span>
            </span>
            <textarea
              value={data.goal}
              onChange={(e) => onChange({ goal: e.target.value })}
              placeholder="What should participants achieve by the end of the session?"
              rows={2}
              className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
            />
          </label>
        </div>
      </div>

      {/* Card 3: Classification */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <LayoutGrid className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-main">Classification</h3>
            <p className="text-sm text-text-sub">Categorize and set the visibility scope for this template.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Category */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-main">
              Category <span className="text-red-500">*</span>
            </span>
            <select
              value={data.category}
              onChange={(e) => onChange({ category: e.target.value as TemplateType })}
              className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
            >
              <option value={TemplateType.WORKSHOP}>Workshop</option>
              <option value={TemplateType.BRAINSTORM}>Brainstorm</option>
              <option value={TemplateType.REVIEW}>Review</option>
              <option value={TemplateType.PLANNING}>Planning</option>
              <option value={TemplateType.CUSTOM}>Custom</option>
            </select>
          </label>

          {/* Scope */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-main">
              Scope <span className="text-red-500">*</span>
            </span>
            <select
              value={data.scope}
              onChange={(e) => onChange({ scope: e.target.value as TemplateScope })}
              className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
            >
              <option value={TemplateScope.SESSION}>Session (one-time use)</option>
              <option value={TemplateScope.USER}>User (personal library)</option>
              <option value={TemplateScope.ORGANIZATION}>Organization (requires approval)</option>
            </select>
          </label>
        </div>

        {/* Tip */}
        <div className="flex items-start gap-3 bg-primary/10 p-4 rounded-lg border border-primary/20 mt-5">
          <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-primary/90">
            Start with User scope for personal templates. You can submit them for organization-wide approval later through the publishing workflow.
          </p>
        </div>
      </div>
    </div>
  );
}
